import ServiceLanding, { type ServiceLandingProps } from "@/components/feature/ServiceLanding";
import { buildBreadcrumbSchema, buildServiceSchema } from "@/seo/schema";
import { SITE_URL } from "@/pages/home/constants";

const PAGE_URL = `${SITE_URL}passeio-de-jet-ski-capitolio`;

const data: ServiceLandingProps = {
  seo: {
    title: "Passeio e Locação de Jet Ski em Capitólio | Furnas",
    description:
      "Passeio de jet ski em Capitólio a partir de R$ 150 para duas pessoas. Locação de moto aquática no Lago de Furnas com instrutor e paradas para fotos.",
    keywords:
      "passeio de jet ski em Capitólio, aluguel de jet ski Capitólio, locação de jet ski Lago de Furnas, moto aquática Capitólio, jet ski Mar de Minas",
    canonical: PAGE_URL,
    jsonLd: [
      buildServiceSchema({
        name: "Passeio de jet ski em Capitólio",
        description:
          "Passeio guiado e locação de jet ski em Capitólio no Lago de Furnas, com instrutor experiente e paradas para fotos nas águas do Mar de Minas.",
        url: PAGE_URL,
        serviceType: "Passeio de jet ski",
        priceFrom: 150,
        priceDescription: "Passeio de jet ski a partir de R$ 150 para duas pessoas",
      }),
      buildBreadcrumbSchema([
        { name: "Início", url: SITE_URL },
        { name: "Passeio de Jet Ski em Capitólio", url: PAGE_URL },
      ]),
    ],
  },
  breadcrumbLabel: "Passeio de Jet Ski em Capitólio",
  tagline: "Passeio e locação de jet ski em Capitólio",
  h1: "Passeio de Jet Ski em Capitólio",
  intro:
    "Passeio de jet ski em Capitólio e locação de moto aquática no Lago de Furnas. Acelere pelo Mar de Minas com instrutor experiente, paradas paradisíacas para fotos e toda a segurança — ou faça a locação de jet ski em Capitólio e explore o lago no seu próprio ritmo.",
  heroVariant: "stacked",
  heroImage:
    "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/6c4f106e4db31c43f929dbc6e7aa6f74.jpeg",
  heroAlt:
    "Piloto cortando a água de jet ski no Lago de Furnas ao pôr do sol em Capitólio, Minas Gerais",
  heroImagePosition: "object-[56%_52%] lg:object-center",
  reviewsSubtitle:
    "Quem já acelerou no Lago de Furnas conta como foi — avaliações reais do Google e do TripAdvisor sobre o passeio de jet ski em Capitólio.",
  priceNote: "A partir de R$ 150 para 2 pessoas",
  highlights: [
    { icon: "ri-compass-3-line", title: "Passeio de jet ski em Capitólio", description: "Guiado por instrutor" },
    { icon: "ri-sailboat-line", title: "Locação de jet ski em Capitólio", description: "Explore no seu ritmo" },
    { icon: "ri-price-tag-3-line", title: "A partir de R$ 150", description: "Para duas pessoas" },
  ],
  featuresTitle: "Passeio e locação de jet ski em Capitólio",
  featuresSubtitle:
    "Uma das experiências mais emocionantes do Mar de Minas: passeio de jet ski em Capitólio com instrutor ou locação de jet ski, sempre com orientação de quem conhece o lago.",
  features: [
    {
      icon: "ri-user-star-line",
      title: "Instrutor experiente",
      description:
        "Todos os passeios guiados contam com acompanhamento de um instrutor que conhece os melhores roteiros do Lago de Furnas.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Segurança em primeiro lugar",
      description:
        "Equipamentos de segurança, coletes salva-vidas e briefing antes de sair, para você pilotar com total tranquilidade.",
    },
    {
      icon: "ri-landscape-line",
      title: "Visuais inesquecíveis",
      description:
        "Acelere entre os cânions, pare em pontos paradisíacos e registre cada momento do seu passeio de jet ski em Capitólio.",
    },
  ],
  stepsTitle: "Como funciona o passeio de jet ski em Capitólio",
  stepsSubtitle: "Do agendamento à adrenalina, tudo simples e organizado.",
  steps: [
    {
      icon: "ri-whatsapp-line",
      title: "Agende pelo WhatsApp",
      description: "Informe a data e a quantidade de pessoas e confirmamos a disponibilidade do passeio ou da locação de jet ski.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Briefing de segurança",
      description: "Antes de sair, você recebe orientações e equipamentos de segurança.",
    },
    {
      icon: "ri-speed-up-line",
      title: "Acelere no lago",
      description: "Pilote com acompanhamento e aproveite o Lago de Furnas com paradas para fotos.",
    },
  ],
  galleryTitle: "Jet ski no Lago de Furnas",
  gallerySubtitle:
    "Fotos reais do passeio de jet ski em Capitólio e da locação de jet ski no Lago de Furnas — o Mar de Minas visto de cima da moto aquática.",
  galleryImageAspect: "aspect-[4/5]",
  galleryVariant: "grid",
  galleryColumns: 4,
  gallery: [
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/84cdc31f1995fe2d0068d89e353e65ce.jpeg",
      alt: "Duas clientes sorrindo a bordo do jet ski no Lago de Furnas em Capitólio",
      caption: "Passeio de jet ski a dois",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/fea71e2d66cedcfb53ae47fde0a5e02f.jpeg",
      alt: "Cliente pilotando jet ski nas águas azuis do Lago de Furnas em Capitólio",
      caption: "Passeio de jet ski no Lago de Furnas",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/dca2088266f7af47db2ed293856e87b4.jpeg",
      alt: "Grupo de clientes sorrindo a bordo do jet ski no Lago de Furnas em Capitólio",
      caption: "Passeio de jet ski em grupo",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/4206fa1e88a8b02c093940326eaf4a66.jpeg",
      alt: "Jet ski cortando a água do Lago de Furnas ao pôr do sol em Capitólio",
      caption: "Jet ski ao pôr do sol",
    },
  ],
  faqs: [
    {
      question: "Quanto custa um passeio de jet ski em Capitólio?",
      answer:
        "O passeio de jet ski em Capitólio sai a partir de R$ 150 para duas pessoas. O valor final pode variar conforme o tempo de uso e a temporada — fale com a Eco Tour Turismo pelo WhatsApp para confirmar os valores atualizados e a disponibilidade.",
    },
    {
      question: "Preciso ter experiência para pilotar o jet ski?",
      answer:
        "Não. No passeio guiado você sai acompanhado de um instrutor experiente e recebe um briefing de segurança antes de pilotar, então tanto iniciantes quanto experientes aproveitam com tranquilidade.",
    },
    {
      question: "O passeio de jet ski é guiado?",
      answer:
        "Sim. Oferecemos passeio guiado com instrutor, que conduz você por roteiros seguros e cheios de visual no Lago de Furnas, com paradas paradisíacas.",
    },
    {
      question: "Posso alugar o jet ski sem guia?",
      answer:
        "Oferecemos a locação de moto aquática para você explorar o Lago de Furnas no seu próprio ritmo. Nossa equipe orienta sobre as regras de navegação e segurança.",
    },
    {
      question: "O passeio para para fotos?",
      answer:
        "Sim. Fotos e vídeos do passeio de jet ski em Capitólio são feitos em paradas em pontos paradisíacos do Lago de Furnas, no coração do Mar de Minas.",
    },
  ],
  ctaTitle: "Quer reservar seu passeio de jet ski em Capitólio?",
  ctaText:
    "Passeio guiado ou locação de jet ski em Capitólio a partir de R$ 150 para duas pessoas. Saídas diárias e atendimento rápido pelo WhatsApp.",
  related: [
    {
      to: "/passeio-de-lancha-capitolio",
      title: "Passeio de Lancha em Capitólio",
      description: "Roteiro guiado pelos cânions, Lagoa Azul e cachoeiras, compartilhado ou exclusivo.",
      icon: "ri-sailboat-line",
    },
    {
      to: "/aluguel-de-lancha-capitolio",
      title: "Aluguel de Lancha em Capitólio",
      description: "Locação de lancha particular para o seu grupo, com ou sem marinheiro, no Lago de Furnas.",
      icon: "ri-ship-2-line",
    },
  ],
};

export default function PasseioDeJetSkiCapitolio() {
  return <ServiceLanding {...data} />;
}